import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCommunityData } from "firebase_/firebase_function/firebase_community";
import { getBlockData } from "firebase_/firebase_function/firebase_user";
import { useUser } from "util/context/user_context";
import TabbarItemMobile from "components/community/tabbar_item_m";
import FeedMobile from "components/community/feed_m";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
};

const TAB_LABELS = ["이야기", "고장문의"];

function tabToType(tab) {
  return tab === "고장문의" ? "고장 문의" : "농사 이야기";
}

function SkeletonRow() {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div style={{ width: "100%", aspectRatio: "16/9", background: "#F3F4F6" }} />
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ height: 12, width: "30%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 16, width: "70%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "60%", background: "#F3F4F6", borderRadius: 6 }} />
      </div>
    </div>
  );
}

export default function MobileCommunityScreen() {
  const navigate = useNavigate();
  const { userData, blockData, setBlockData } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTab = searchParams.get("category") || "이야기";
  const type = tabToType(currentTab);

  // 탭별로 데이터/커서/끝났는지 분리
  const [tabState, setTabState] = useState({
    이야기: { data: [], lastCreateDate: "", hasMore: true },
    고장문의: { data: [], lastCreateDate: "", hasMore: true },
  });
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const observer = useRef();
  const initRef = useRef(false);

  // 차단 + 첫 로드
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const block = userData?.docId ? await getBlockData(userData.docId) : [];
        if (cancelled) return;
        setBlockData(block || []);
        if (!initRef.current) {
          initRef.current = true;
          await fetchData(currentTab, block || [], true);
        }
      } catch {
        if (!cancelled) setBlockData([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.docId]);

  // 탭 변경 시: 해당 탭 비어있으면 가져오기
  useEffect(() => {
    if (!initRef.current) return;
    if (tabState[currentTab].data.length === 0 && tabState[currentTab].hasMore) {
      fetchData(currentTab, blockData || [], true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const fetchData = async (tab, block, isFirst = false) => {
    if (!isFirst && !tabState[tab].hasMore) return;
    if (loading) return;
    if (isFirst) setInitLoading(true);
    setLoading(true);

    const lastDate = isFirst ? "" : tabState[tab].lastCreateDate;
    const list = await getCommunityData(tabToType(tab), 5, lastDate, block || []);

    if (Array.isArray(list)) {
      setTabState((prev) => ({
        ...prev,
        [tab]: {
          data: isFirst ? list : [...prev[tab].data, ...list],
          lastCreateDate: list[list.length - 1]?.createDate || "",
          hasMore: list.length > 0,
        },
      }));
    }
    setLoading(false);
    if (isFirst) setInitLoading(false);
  };

  const lastFeedRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && tabState[currentTab].hasMore) {
          fetchData(currentTab, blockData || [], false);
        }
      });
      if (node) observer.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, tabState, currentTab, blockData],
  );

  const onSelectTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set("category", tab);
    setSearchParams(next);
  };

  const onWrite = () => navigate("/community/write");
  const items = tabState[currentTab].data;

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        padding: "16px 16px 32px",
        background: C.bg,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
            커뮤니티
          </div>
          <div style={{ marginTop: 2, fontSize: 12, color: C.sub }}>
            농가들과 정보를 나누고 궁금증을 해결하세요.
          </div>
        </div>
        <button
          type="button"
          onClick={onWrite}
          style={{
            padding: "8px 14px",
            borderRadius: 9999,
            background: C.greenDark,
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          + 글쓰기
        </button>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        {TAB_LABELS.map((t) => (
          <TabbarItemMobile key={t} currentTab={currentTab} item={t} onClick={() => onSelectTab(t)} />
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {initLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : items.length > 0 ? (
          items.map((story, index) => {
            const isLast = index === items.length - 1;
            return (
              <div key={story.docId || index} ref={isLast ? lastFeedRef : null}>
                <FeedMobile story={story} />
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: "48px 16px",
              background: "#fff",
              borderRadius: 14,
              border: `1px dashed ${C.line}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 9999,
                background: C.greenBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
              }}
            >
              ✏️
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
              아직 작성된 글이 없어요
            </div>
            <div style={{ fontSize: 12, color: C.sub, textAlign: "center" }}>
              첫 번째 글을 작성해보세요.
            </div>
          </div>
        )}
        {!initLoading && loading && <SkeletonRow />}
      </div>
    </div>
  );
}
